import { useMergeLink } from '@mergeapi/react-merge-link';
import * as Api from '@ViewModels';
import * as React from 'react';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useCreateMergeLinkToken, useExchangeMergePublicTokenForAccountTokenMutation } from '../../../../queries';

export function useMergeAuth({
	errorLoggingCategory,
	integrationName,
	integrationSource,
	onError,
	onSuccess,
	userSession,
}: {
	errorLoggingCategory?: string;
	integrationName: string;
	integrationSource: Api.IntegrationSources;
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
	userSession: Api.UserSessionContext;
}) {
	const { data: linkToken } = useCreateMergeLinkToken({
		integrationName,
		integrationSource,
		refetchOnWindowFocus: false,

		userSession,
	});

	const { mutateAsync } = useExchangeMergePublicTokenForAccountTokenMutation({ userSession });
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging(errorLoggingCategory || 'useMergeAuth');

	const onMergeLinkSuccess = React.useCallback(
		async (token: string) => {
			try {
				await mutateAsync({
					integration: integrationName,
					integrationProvider: integrationSource,
					publicToken: token,
				});
				onSuccess?.();
			} catch (err) {
				errorMessages.pushApiError(err);

				logApiError('ExchangeMergePublicTokenForAccountToken-Error', err);

				onError?.(err);
			}
		},
		[mutateAsync, integrationName, integrationSource, onSuccess, errorMessages, logApiError, onError]
	);

	const { open, isReady: isMergeLinkReady } = useMergeLink({
		linkToken,
		onSuccess: onMergeLinkSuccess,
	});

	return { isReady: isMergeLinkReady && linkToken, openMergeAuthFlow: open } as const;
}
